import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { SavedWikipedia, WikipediaInput, WikipediaResult } from '@creanizer/types'
import { createIndexedDBStorage } from '@creanizer/utils'

interface WikipediaStore {
  savedWikipedias: SavedWikipedia[]
  addWikipedia: (data: {
    title: string
    input: WikipediaInput
    result: WikipediaResult
    memo?: string
    tags?: string[]
  }) => void
  updateWikipedia: (id: string, updates: Partial<SavedWikipedia>) => void
  deleteWikipedia: (id: string) => void
  getWikipedia: (id: string) => SavedWikipedia | undefined
  exportAsText: () => void
  exportAsJSON: () => void
  exportSingleAsText: (id: string) => void
  exportSingleAsJSON: (id: string) => void
  importFromJSON: (jsonString: string) => void
  clearWikipedias: () => void
}

const MAX_SAVED_WIKIPEDIAS = 500

export const useWikipediaStore = create<WikipediaStore>()(
  persist(
    (set, get) => ({
      savedWikipedias: [],

      addWikipedia: (data) => {
        const newWikipedia: SavedWikipedia = {
          id: crypto.randomUUID(),
          title: data.title,
          input: data.input,
          result: data.result,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          memo: data.memo,
          tags: data.tags
        }

        set((state) => {
          const updated = [newWikipedia, ...state.savedWikipedias]
          if (updated.length > MAX_SAVED_WIKIPEDIAS) {
            updated.pop()
            alert(`保存件数が上限（${MAX_SAVED_WIKIPEDIAS}件）に達したため、最も古い記事を削除しました。`)
          }
          return { savedWikipedias: updated }
        })
      },

      updateWikipedia: (id, updates) => {
        set((state) => ({
          savedWikipedias: state.savedWikipedias.map((item) =>
            item.id === id
              ? {
                  ...item,
                  ...updates,
                  updatedAt: new Date().toISOString()
                }
              : item
          )
        }))
      },

      deleteWikipedia: (id) => {
        set((state) => ({
          savedWikipedias: state.savedWikipedias.filter((item) => item.id !== id)
        }))
      },

      getWikipedia: (id) => {
        return get().savedWikipedias.find((item) => item.id === id)
      },

      exportAsText: () => {
        const { savedWikipedias } = get()
        if (savedWikipedias.length === 0) {
          alert('保存された架空事典がありません。')
          return
        }

        const content = formatWikipediasForExport(savedWikipedias)
        downloadTextFile(content, `saved_wikipedias_${Date.now()}.txt`)
      },

      exportAsJSON: () => {
        const { savedWikipedias } = get()
        if (savedWikipedias.length === 0) {
          alert('保存された架空事典がありません。')
          return
        }

        const exportData = {
          version: '1.0.0',
          exportDate: new Date().toISOString(),
          dataType: 'wikipedias',
          data: savedWikipedias
        }

        downloadJSONFile(exportData, `saved_wikipedias_${Date.now()}.json`)
      },

      importFromJSON: (jsonString: string) => {
        try {
          const importData = JSON.parse(jsonString)
          
          // バリデーション
          if (!importData.data && !importData.wikipedias) {
            throw new Error('無効なデータ形式です')
          }

          const newWikipedias = (importData.data || importData.wikipedias) as SavedWikipedia[]
          const currentWikipedias = get().savedWikipedias
          
          // 重複チェック（タイトルと項目名で判定）
          const uniqueWikipedias = newWikipedias.filter(newWiki => {
            return !currentWikipedias.some(existing => 
              existing.title === newWiki.title && 
              existing.result.title === newWiki.result.title
            )
          })
          
          // 最大保存数チェック
          const totalCount = currentWikipedias.length + uniqueWikipedias.length
          if (totalCount > MAX_SAVED_WIKIPEDIAS) {
            const availableSlots = MAX_SAVED_WIKIPEDIAS - currentWikipedias.length
            if (availableSlots <= 0) {
              alert(`保存数が上限（${MAX_SAVED_WIKIPEDIAS}件）に達しています。不要な架空事典を削除してください。`)
              return
            }
            if (!confirm(`追加可能な件数は${availableSlots}件です。${uniqueWikipedias.length}件中${availableSlots}件のみインポートしますか？`)) {
              return
            }
            uniqueWikipedias.splice(availableSlots)
          }

          // IDの重複を避けるため、新しいIDを生成
          const importedWikipedias = uniqueWikipedias.map(wiki => ({
            ...wiki,
            id: crypto.randomUUID(),
            createdAt: wiki.createdAt || new Date().toISOString(),
            updatedAt: wiki.updatedAt || new Date().toISOString()
          }))

          const mergedWikipedias = [...currentWikipedias, ...importedWikipedias]
          set({ savedWikipedias: mergedWikipedias })
          
          const skippedCount = newWikipedias.length - uniqueWikipedias.length
          alert(`${importedWikipedias.length}件の架空事典をインポートしました。${skippedCount > 0 ? `(${skippedCount}件の重複をスキップ)` : ''}`)
        } catch (error) {
          console.error('Import error:', error)
          alert('インポートに失敗しました。ファイル形式を確認してください。')
        }
      },

      exportSingleAsText: (id: string) => {
        const wikipedia = get().getWikipedia(id)
        if (!wikipedia) {
          alert('架空事典が見つかりません。')
          return
        }
        
        const content = formatWikipediasForExport([wikipedia])
        downloadTextFile(content, `wikipedia_${wikipedia.result.title}_${Date.now()}.txt`)
      },

      exportSingleAsJSON: (id: string) => {
        const wikipedia = get().getWikipedia(id)
        if (!wikipedia) {
          alert('架空事典が見つかりません。')
          return
        }
        
        const exportData = {
          version: '1.0.0',
          exportDate: new Date().toISOString(),
          dataType: 'wikipedia',
          data: wikipedia
        }
        
        downloadJSONFile(exportData, `wikipedia_${wikipedia.result.title}_${Date.now()}.json`)
      },

      clearWikipedias: () => {
        if (confirm('保存済みの架空事典記事をすべて削除しますか？')) {
          set({ savedWikipedias: [] })
        }
      }
    }),
    {
      name: 'creanizer-wikipedias',
      storage: createIndexedDBStorage('creanizer-wikipedias'),
      partialize: (state) => ({ savedWikipedias: state.savedWikipedias }),
      migrate: (persistedState: any, version: number) => {
        // バージョン0（初期状態）からバージョン1への移行
        if (version === 0) {
          // 古いデータ構造から新しいデータ構造への変換
          return persistedState
        }
        return persistedState
      }
    }
  )
)

// 架空事典をテキスト形式にフォーマット
function formatWikipediasForExport(wikipedias: SavedWikipedia[]): string {
  const date = new Date().toLocaleString('ja-JP')
  
  let content = `【保存された架空事典一覧】
生成日時: ${date}
総数: ${wikipedias.length}件

`

  wikipedias.forEach((wiki, index) => {
    const { result } = wiki
    content += `\n【${index + 1}】 ${result.title}\n`
    content += '=' .repeat(60) + '\n\n'
    
    // 基本情報
    content += `カテゴリ: ${result.category}\n`
    if (result.categories && result.categories.length > 0) {
      content += `分類: ${result.categories.join('、')}\n`
    }
    content += '\n'
    
    // インフォボックス
    if (result.infobox && Object.keys(result.infobox).length > 0) {
      content += `基本情報:\n`
      Object.entries(result.infobox).forEach(([key, value]) => {
        content += `  ${key}: ${value}\n`
      })
      content += '\n'
    }
    
    // リードセクション
    content += `概要:\n${result.leadSection}\n\n`
    
    // 各セクション
    if (result.sections && result.sections.length > 0) {
      result.sections.forEach(section => {
        content += `■ ${section.title}\n`
        content += `${section.content}\n\n`
        
        if (section.subsections && section.subsections.length > 0) {
          section.subsections.forEach(subsection => {
            content += `  ▪ ${subsection.title}\n`
            content += `  ${subsection.content}\n\n`
          })
        }
      })
    }
    
    // 関連項目
    if (result.seeAlso && result.seeAlso.length > 0) {
      content += `関連項目:\n`
      result.seeAlso.forEach(item => {
        content += `  - ${item}\n`
      })
      content += '\n'
    }
    
    // 参考文献
    if (result.references && result.references.length > 0) {
      content += `参考文献:\n`
      result.references.forEach((ref, i) => {
        content += `  [${i + 1}] ${ref}\n`
      })
      content += '\n'
    }
    
    // メモとタグ
    if (wiki.memo) {
      content += `メモ: ${wiki.memo}\n`
    }
    
    if (wiki.tags && wiki.tags.length > 0) {
      content += `タグ: ${wiki.tags.join(', ')}\n`
    }
    
    content += '\n'
  })

  return content
}

// テキストファイルをダウンロード
function downloadTextFile(content: string, filename: string) {
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

// JSONファイルをダウンロード
function downloadJSONFile(data: any, filename: string) {
  const jsonString = JSON.stringify(data, null, 2)
  const blob = new Blob([jsonString], { type: 'application/json;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}